import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import Select from '../Select/Select'
import getPageNumbers from './getPageNumbers'
import PageButton from './PageButton'

const CONTAINER = 'tw:flex tw:items-center tw:justify-between tw:gap-4'
const PAGES_GROUP = 'tw:flex tw:items-center tw:border tw:border-solid '
  + 'tw:border-gray-300 tw:rounded-sm tw:overflow-hidden'
const NAV_BUTTON = 'tw:flex tw:items-center tw:justify-center tw:h-8 tw:w-8 '
  + 'tw:bg-white tw:text-gray-500 tw:border-0 tw:cursor-pointer '
  + 'tw:hover:bg-gray-100 '
  + 'tw:disabled:opacity-50 tw:disabled:cursor-not-allowed'
const NAV_PREV = `${NAV_BUTTON} tw:border-r tw:border-solid tw:border-gray-300`
const NAV_NEXT = `${NAV_BUTTON} tw:border-l tw:border-solid tw:border-gray-300`
const PER_PAGE_GROUP = 'tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-gray-500'
const COUNTER = 'tw:text-sm tw:text-gray-500'
const RIGHT_GROUP = 'tw:flex tw:items-center tw:gap-4'

const formatRangeLabel = (page, perPage, total) => {
  const start = (page - 1) * perPage + 1
  const end = Math.min(page * perPage, total)
  return `${start}–${end} sur ${total.toLocaleString('fr-FR')}`
}

const Pagination = ({
  page, perPage, total, lastPage, perPageOptions, perPageLabel,
  onPageChange, onPerPageChange, disabled = false, alwaysVisible = false
}) => {
  const pageNumbers = getPageNumbers(page, lastPage)
  const minPerPage = Math.min(...perPageOptions.map(option => option.value))

  const handlePerPageChange = useCallback(option => {
    onPerPageChange(option.value)
  }, [onPerPageChange])

  const goPrev = useCallback(() => onPageChange(page - 1), [onPageChange, page])
  const goNext = useCallback(() => onPageChange(page + 1), [onPageChange, page])

  const perPageValue = perPageOptions.find(option => option.value === perPage)
    || perPageOptions[0]

  if (!alwaysVisible && total <= minPerPage)
    return null

  const prevDisabled = disabled || page <= 1
  const nextDisabled = disabled || page >= lastPage

  return (
    <div className={CONTAINER}>
      <div className={PER_PAGE_GROUP}>
        <span>{perPageLabel}</span>
        <div className="tw:w-20">
          <Select
            isDisabled={disabled}
            menuPlacement="top"
            options={perPageOptions}
            value={perPageValue}
            isSearchable={false}
            onChange={handlePerPageChange}
          />
        </div>
      </div>
      <div className={RIGHT_GROUP}>
        <span className={COUNTER}>{formatRangeLabel(page, perPage, total)}</span>
        <div className={PAGES_GROUP}>
          <button
            aria-label="Page précédente"
            className={NAV_PREV}
            disabled={prevDisabled}
            type="button"
            onClick={goPrev}
          >
            <i className="fas fa-chevron-left" />
          </button>
          {pageNumbers.map((pageNumber, index) => (
            <PageButton
              key={`${pageNumber}-${index}`}
              disabled={disabled}
              isActive={pageNumber === page}
              page={pageNumber}
              onClick={onPageChange}
            />
          ))}
          <button
            aria-label="Page suivante"
            className={NAV_NEXT}
            disabled={nextDisabled}
            type="button"
            onClick={goNext}
          >
            <i className="fas fa-chevron-right" />
          </button>
        </div>
      </div>
    </div>
  )
}

Pagination.propTypes = {
  alwaysVisible: PropTypes.bool,
  disabled: PropTypes.bool,
  lastPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPerPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  perPageLabel: PropTypes.string,
  perPageOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired
  })).isRequired,
  total: PropTypes.number.isRequired
}

Pagination.defaultProps = {
  alwaysVisible: false,
  disabled: false,
  perPageLabel: 'Lignes par page :'
}

export default Pagination
